import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Drawer, Fab, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/CloseRounded';
import DateRangeIcon from '@material-ui/icons/DateRangeRounded';
import MinimizeIcon from '@material-ui/icons/ChevronRightRounded';
import { isSecureWebUrl, isValidWebUrl } from 'utils/functions';

interface ScriptAppointmentProps {
  appointmentUrl: string;
  attention: boolean;
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginRight: '2px',
  },
  button: {
    padding: 0,
  },
  buttonAttention: {
    animationName: '$wobble',
    animationDuration: '1.5s',
    animationTimingFunction: 'ease',
    animationIterationCount: 'infinite',
    animationFillMode: 'forwards',
    'transform-origin': '50% 50%',
  },
  drawerMinimizeIcon: {
    position: 'absolute',
    bottom: theme.spacing(8),
    right: theme.spacing(2),
  },
  drawerCloseIcon: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  paper: {
    width: '50vh',
    maxWidth: '500px',
  },

  '@keyframes wobble': {
    '0%': {
      transform: 'translateX(0%)',
    },
    '15%': {
      transform: 'translateX(-30px) rotate(-6deg)',
    },
    '30%': {
      transform: 'translateX(15px) rotate(6deg)',
    },
    '45%': {
      transform: 'translateX(-15px) rotate(-3.6deg)',
    },
    '60%': {
      transform: 'translateX(9px) rotate(2.4deg)',
    },
    '75%': {
      transform: 'translateX(-6px) rotate(-1.2deg)',
    },
    '100%': {
      transform: 'translateX(0 %)',
    },
  },
}));

const ScriptAppointment = ({ appointmentUrl, attention }: ScriptAppointmentProps) => {
  const styles = useStyles();

  const [bringAttention, setBringAttention] = useState(attention);

  // content can be unloaded independently of the drawer open/closed state
  const [loadContent, setLoadContent] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    setBringAttention(attention);
  }, [attention]);

  // check for valid URL before continuing
  if (!isValidWebUrl(appointmentUrl)) return null;

  const loadAndOpenDrawer = () => {
    setLoadContent(true);
    setOpenDrawer(true);
  };

  const unloadAndCloseDrawer = () => {
    setLoadContent(false);
    setOpenDrawer(false);
  };

  const handleClick = (e: any) => {
    // secure web URLs should open in the drawer
    // all other URLs should open in a new tab
    if (isSecureWebUrl(appointmentUrl)) {
      e.preventDefault();
      loadAndOpenDrawer();
    }
  };

  return (
    <div className={styles.container}>
      <Tooltip title="Book Appointment" placement="left" arrow>
        <IconButton
          className={cx(styles.button, {
            [styles.buttonAttention]: bringAttention,
          })}
          color="secondary"
          href={appointmentUrl}
          onClick={(e) => handleClick(e)}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          onMouseOver={() => setBringAttention(false)}
        >
          <DateRangeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Drawer
        variant="persistent"
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        ModalProps={{
          hideBackdrop: true,
        }}
        classes={{ paper: styles.paper }}
      >
        {loadContent && (
          <iframe
            title="Book an Appointment"
            src={appointmentUrl}
            height="100%"
            frameBorder="0"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        )}
        {openDrawer && (
          <>
            <Tooltip title="Minimize" placement="left" arrow>
              <Fab
                size="small"
                className={styles.drawerMinimizeIcon}
                aria-label="minimize"
                onClick={() => setOpenDrawer(false)}
              >
                <MinimizeIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Close" placement="left" arrow>
              <Fab
                size="small"
                color="secondary"
                className={styles.drawerCloseIcon}
                aria-label="close"
                onClick={() => unloadAndCloseDrawer()}
              >
                <CloseIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ScriptAppointment;
